/**
 * Auth context. Tracks the Firebase user and derives role + customerId from the
 * ID token's custom claims (set server-side by the API when a user is invited).
 * Exposes sign-in (Google SSO for ops, email/password for customers) and sign-out.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import type { UserRole } from '../api/types';

interface AuthState {
  user: User | null;
  role: UserRole | null;
  customerId: string | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onIdTokenChanged(
      auth,
      async (next) => {
        setUser(next);
        if (next) {
          const { claims } = await next.getIdTokenResult();
          const claimRole = claims.role;
          const claimCustomer = claims.customerId;
          setRole(claimRole === 'admin' || claimRole === 'customer' ? claimRole : null);
          setCustomerId(typeof claimCustomer === 'string' ? claimCustomer : null);
        } else {
          setRole(null);
          setCustomerId(null);
        }
        setLoading(false);
      },
      // Degrade gracefully if auth init/observer errors — never hang on the spinner.
      () => {
        setUser(null);
        setRole(null);
        setCustomerId(null);
        setLoading(false);
      },
    );
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      role,
      customerId,
      loading,
      signInWithGoogle: async () => {
        await signInWithPopup(auth, googleProvider);
      },
      signInWithEmail: async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOut: async () => {
        await firebaseSignOut(auth);
      },
    }),
    [user, role, customerId, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
